import { useEffect, useState } from 'react';
import { Plus, Pencil, Trash2, Power, Check, Globe, Lock } from 'lucide-react';
import { useTranslation } from '../../../i18n';
import { Button } from '../../../components/Shared/Button';
import { Card } from '../../../components/Shared/Card';
import { Badge } from '../../../components/Shared/Badge';
import { EmptyState } from '../../../components/Shared/EmptyState';
import { Modal } from '../../../components/Shared/Modal';
import { ConfirmDialog } from '../../../components/Shared/ConfirmDialog';
import { useSubscriptionPlanStore } from '../../../store/useSubscriptionPlanStore';
import { PlanForm } from './components/PlanForm';
import { formatMoney } from '../../../utils/money';
import {
  BusinessPlan,
  type CreateSubscriptionPlanRequest,
  type SubscriptionPlanDto,
  type UpdateSubscriptionPlanRequest,
} from '../../../api/types/admin';

function planColor(bp: BusinessPlan | undefined): 'gray' | 'blue' | 'green' | 'purple' {
  switch (bp) {
    case BusinessPlan.Starter:
      return 'blue';
    case BusinessPlan.Professional:
      return 'green';
    case BusinessPlan.Enterprise:
      return 'purple';
    case BusinessPlan.Free:
    default:
      return 'gray';
  }
}

export default function SubscriptionPlansPage() {
  const { t } = useTranslation(['admin', 'common']);
  const { plans, loading, saving, fetchAll, create, update, setActive, remove } =
    useSubscriptionPlanStore();

  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<SubscriptionPlanDto | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<SubscriptionPlanDto | null>(null);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const takenPlans = plans
    .map((p) => p.businessPlan as BusinessPlan | undefined)
    .filter((bp): bp is BusinessPlan => bp !== undefined);

  const handleCreate = async (data: CreateSubscriptionPlanRequest | UpdateSubscriptionPlanRequest) => {
    const ok = await create(data as CreateSubscriptionPlanRequest);
    if (ok) setCreateOpen(false);
    return Boolean(ok);
  };

  const handleUpdate = async (
    data: CreateSubscriptionPlanRequest | UpdateSubscriptionPlanRequest,
  ) => {
    if (!editing?.id) return false;
    const ok = await update(editing.id, data as UpdateSubscriptionPlanRequest);
    if (ok) setEditing(null);
    return ok;
  };

  const handleDeleteConfirm = async () => {
    if (!confirmDelete?.id) return;
    const ok = await remove(confirmDelete.id);
    if (ok) setConfirmDelete(null);
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="rounded-xl border border-slate-200 bg-white p-4 sm:p-5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-[22px] sm:text-[24px] font-semibold tracking-tight text-slate-900">
              {t('admin:plans.title')}
            </h1>
            <p className="text-[13px] text-slate-500 mt-0.5">{t('admin:plans.subtitle')}</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 shrink-0">
            <Button
              onClick={() => setCreateOpen(true)}
              leftIcon={<Plus className="w-4 h-4" strokeWidth={2} />}
              className="w-full sm:w-auto justify-center"
            >
              {t('admin:plans.actions.create')}
            </Button>
          </div>
        </div>
      </div>

      {loading && plans.length === 0 ? (
        <Card>
          <p className="text-[13px] text-slate-500 text-center py-6">{t('common:actions.loading')}</p>
        </Card>
      ) : plans.length === 0 ? (
        <Card>
          <EmptyState title={t('admin:plans.empty')} />
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {plans.map((plan) => {
            const bp = plan.businessPlan as BusinessPlan | undefined;
            const planKey = bp != null ? String(bp) : '0';
            const planLabel = t(`admin:tenants.planNames.${planKey}`);
            const priceLabel =
              plan.price == null
                ? t('admin:plans.card.onRequest')
                : formatMoney(plan.price, { currency: plan.currency, wholeUnits: true });
            const employeesLabel =
              plan.maxEmployees == null
                ? t('admin:plans.card.unlimited')
                : t('admin:plans.card.maxEmployees', { count: plan.maxEmployees });

            return (
              <div
                key={plan.id}
                className={`rounded-xl border bg-white flex flex-col ${
                  plan.isActive ? 'border-slate-200' : 'border-slate-200 opacity-70'
                }`}
              >
                <div className="px-4 py-3 border-b border-slate-100">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <Badge color={planColor(bp)} className="mb-1">
                        {planLabel}
                      </Badge>
                      <h3 className="text-[15px] font-semibold text-slate-900 tracking-tight">
                        {plan.name}
                      </h3>
                    </div>
                    {!plan.isActive && (
                      <Badge variant="default">{t('admin:plans.card.inactive')}</Badge>
                    )}
                  </div>
                </div>

                <div className="px-4 py-3 flex-1 space-y-3">
                  <div>
                    <div className="text-[28px] font-mono font-semibold tracking-tight text-slate-900 tabular-nums">
                      {priceLabel}
                    </div>
                    {plan.price != null && (
                      <div className="text-[12px] text-slate-500">
                        {t('admin:plans.card.perMonth')}
                      </div>
                    )}
                  </div>

                  <div className="text-[12px] text-slate-600">{employeesLabel}</div>

                  {plan.description && (
                    <p className="text-[12px] text-slate-500">{plan.description}</p>
                  )}

                  {plan.features && plan.features.length > 0 && (
                    <ul className="space-y-1">
                      {plan.features.map((f) => (
                        <li key={f} className="flex items-start gap-1.5 text-[12px] text-slate-700">
                          <Check className="w-3 h-3 mt-0.5 text-[#00A871] flex-shrink-0" strokeWidth={3} />
                          <span>{f}</span>
                        </li>
                      ))}
                    </ul>
                  )}

                  <div className="flex items-center gap-1.5 text-[11px] text-slate-500">
                    {plan.isPublic ? (
                      <>
                        <Globe className="w-3 h-3" strokeWidth={2} />
                        {t('admin:plans.card.public')}
                      </>
                    ) : (
                      <>
                        <Lock className="w-3 h-3" strokeWidth={2} />
                        {t('admin:plans.card.internal')}
                      </>
                    )}
                  </div>
                </div>

                <div className="px-4 py-3 border-t border-slate-100 flex items-center justify-between gap-2">
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      title={t('admin:plans.actions.edit')}
                      onClick={() => setEditing(plan)}
                      className="p-1.5"
                    >
                      <Pencil className="w-3.5 h-3.5" strokeWidth={2} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      title={
                        plan.isActive
                          ? t('admin:plans.actions.deactivate')
                          : t('admin:plans.actions.activate')
                      }
                      onClick={() => plan.id && setActive(plan.id, !plan.isActive)}
                      disabled={saving}
                      className="p-1.5"
                    >
                      <Power className="w-3.5 h-3.5" strokeWidth={2} />
                    </Button>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    title={t('admin:plans.actions.delete')}
                    onClick={() => setConfirmDelete(plan)}
                    className="p-1.5"
                  >
                    <Trash2 className="w-3.5 h-3.5 text-red-600" strokeWidth={2} />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Modal
        isOpen={createOpen}
        onClose={() => setCreateOpen(false)}
        title={t('admin:plans.form.createTitle')}
        size="lg"
      >
        <PlanForm
          takenPlans={takenPlans}
          onSubmit={handleCreate}
          onCancel={() => setCreateOpen(false)}
          loading={saving}
        />
      </Modal>

      <Modal
        isOpen={Boolean(editing)}
        onClose={() => setEditing(null)}
        title={t('admin:plans.form.editTitle')}
        size="lg"
      >
        {editing && (
          <PlanForm
            initial={editing}
            takenPlans={takenPlans}
            onSubmit={handleUpdate}
            onCancel={() => setEditing(null)}
            loading={saving}
          />
        )}
      </Modal>

      <ConfirmDialog
        isOpen={Boolean(confirmDelete)}
        onClose={() => setConfirmDelete(null)}
        onConfirm={handleDeleteConfirm}
        title={t('admin:plans.confirm.delete.title')}
        message={t('admin:plans.confirm.delete.message')}
        loading={saving}
      />
    </div>
  );
}
